import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AlertCircle, Check, LoaderCircle, Plus, Search, X } from 'lucide-react';
import EventFinderAPI, { ApiRequestError } from '../services/api';
import type { EntitySuggestion, EventGroupChallenge } from '../types/api';
import EntityAvatar from '../components/EntityAvatar';
import { SEO } from '../components/SEO';
import { useAnalytics } from '../hooks/useAnalytics';
import { collectEventGroupClientTelemetry } from '../utils/eventGroupTelemetry';
import { displayEntityType } from '../utils/entityTerminology';
import { colors, spacing, typography, borderRadius, breakpoints, shadows } from '../styles/theme';

const MAX_MEMBERS = 50;
const MIN_MEMBERS = 2;
const PRIVACY_NOTICE_VERSION = '2026-08-22';

const Page = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: ${spacing[8]} ${spacing[4]} ${spacing[16]};

  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[5]} ${spacing[3]} ${spacing[12]};
  }
`;

const Heading = styled.h1`
  margin: 0;
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes['3xl']};
`;

const Intro = styled.p`
  max-width: 680px;
  margin: ${spacing[2]} 0 ${spacing[8]};
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeights.relaxed};
`;

const SearchArea = styled.div`
  position: relative;
  max-width: 680px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${spacing[2]};
  color: ${colors.text.primary};
  font-weight: ${typography.fontWeights.semibold};
`;

const SearchShell = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  min-height: 48px;
  padding: 0 ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};

  &:focus-within {
    border-color: ${colors.border.focus};
    box-shadow: 0 0 0 2px ${colors.primary[100]};
  }

  input {
    flex: 1;
    min-width: 0;
    height: 46px;
    border: 0;
    outline: 0;
    color: ${colors.text.primary};
    font-size: ${typography.fontSizes.base};
  }
`;

const Results = styled.ul`
  position: absolute;
  z-index: 1000;
  top: calc(100% + ${spacing[1]});
  left: 0;
  right: 0;
  max-height: 360px;
  overflow-y: auto;
  margin: 0;
  padding: ${spacing[1]};
  list-style: none;
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
  box-shadow: ${shadows.md};
`;

const ResultButton = styled.button.withConfig({
  shouldForwardProp: prop => prop !== 'isHighlighted',
})<{ isHighlighted: boolean }>`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  align-items: center;
  gap: ${spacing[3]};
  width: 100%;
  min-height: 56px;
  padding: ${spacing[2]};
  border: 0;
  border-radius: ${borderRadius.md};
  background: ${props => props.isHighlighted ? colors.primary[50] : colors.white};
  color: ${colors.text.primary};
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${colors.primary[50]};
    outline: none;
  }
`;

const EntityText = styled.span`
  min-width: 0;
`;

const EntityName = styled.strong`
  display: block;
  overflow-wrap: anywhere;
`;

const EntityMeta = styled.span`
  display: block;
  margin-top: 2px;
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSizes.sm};
`;

const Count = styled.span`
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSizes.sm};
  white-space: nowrap;
`;

const SelectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing[3]};
  max-width: 680px;
  margin-top: ${spacing[8]};
  padding-bottom: ${spacing[2]};
  border-bottom: 1px solid ${colors.border.light};
`;

const SelectionTitle = styled.h2`
  margin: 0;
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.lg};
`;

const SelectionList = styled.ul`
  max-width: 680px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const SelectionRow = styled.li`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 44px;
  align-items: center;
  gap: ${spacing[3]};
  min-height: 64px;
  border-bottom: 1px solid ${colors.border.light};
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: ${borderRadius.md};
  background: transparent;
  color: ${colors.text.secondary};
  cursor: pointer;

  &:hover {
    background: ${colors.error[50]};
    color: ${colors.error[700]};
  }

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
  }
`;

const EmptySelection = styled.p`
  max-width: 680px;
  margin: ${spacing[4]} 0;
  color: ${colors.text.tertiary};
`;

const Submission = styled.div`
  max-width: 680px;
  margin-top: ${spacing[8]};
  padding-top: ${spacing[5]};
  border-top: 1px solid ${colors.border.light};
`;

const Notice = styled.p`
  margin: 0 0 ${spacing[4]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  line-height: ${typography.lineHeights.relaxed};

  a {
    color: ${colors.text.link};
  }
`;

const ErrorNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[4]};
  padding: ${spacing[3]};
  border: 1px solid ${colors.error[200]};
  border-radius: ${borderRadius.md};
  background: ${colors.error[50]};
  color: ${colors.error[800]};
`;

const CreateButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  min-width: 190px;
  min-height: 48px;
  padding: 0 ${spacing[5]};
  border: 0;
  border-radius: ${borderRadius.md};
  background: ${colors.primary[600]};
  color: ${colors.white};
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.semibold};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${colors.primary[700]};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${colors.border.focus};
    outline-offset: 2px;
  }

  svg.spin {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Honeypot = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

function newRequestId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const CreateEventGroupPage: React.FC = () => {
  const navigate = useNavigate();
  const { trackEvent, trackApplicationInsightsEvent } = useAnalytics();
  const [challenge, setChallenge] = useState<EventGroupChallenge | null>(null);
  const [challengeReady, setChallengeReady] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<EntitySuggestion[]>([]);
  const [selected, setSelected] = useState<EntitySuggestion[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [website, setWebsite] = useState('');
  const startedTracked = useRef(false);
  const counters = useRef({
    formStartedAt: performance.now(),
    requestStartedAt: performance.now(),
    searchCount: 0,
    addCount: 0,
    removeCount: 0,
  });

  const loadChallenge = useCallback(async () => {
    setChallengeReady(false);
    const next = await EventFinderAPI.getEventGroupChallenge();
    setChallenge(next);
    window.setTimeout(() => setChallengeReady(true), next.minimumAgeMs);
  }, []);

  useEffect(() => {
    loadChallenge().catch(() => setError('Event Group creation is temporarily unavailable.'));
  }, [loadChallenge]);

  const markStarted = useCallback(() => {
    if (startedTracked.current) return;
    startedTracked.current = true;
    try {
      trackEvent({ action: 'create_started', category: 'event_group', custom_parameters: { action_location: 'entity_selector' } });
    } catch {
      // Analytics is best-effort and must not affect creation.
    }
  }, [trackEvent]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2 || selected.length >= MAX_MEMBERS) {
      setSuggestions([]);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setSearching(true);
      counters.current.searchCount += 1;
      try {
        const results = await EventFinderAPI.eventGroupEntitySuggest(trimmed, 12, controller.signal);
        const selectedIds = new Set(selected.map(entity => entity.entityId));
        setSuggestions(results.filter(entity => !selectedIds.has(entity.entityId)));
        setHighlightedIndex(-1);
      } catch (searchError) {
        if ((searchError as Error).name !== 'AbortError') setSuggestions([]);
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, selected]);

  const addEntity = useCallback((entity: EntitySuggestion) => {
    if (selected.length >= MAX_MEMBERS || selected.some(item => item.entityId === entity.entityId)) return;
    markStarted();
    counters.current.addCount += 1;
    setSelected(current => [...current, entity]);
    setQuery('');
    setSuggestions([]);
  }, [markStarted, selected]);

  const removeEntity = useCallback((entityId: number) => {
    markStarted();
    counters.current.removeCount += 1;
    setSelected(current => current.filter(entity => entity.entityId !== entityId));
  }, [markStarted]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(index => Math.min(index + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(index => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && highlightedIndex >= 0) {
      event.preventDefault();
      addEntity(suggestions[highlightedIndex]);
    } else if (event.key === 'Escape') {
      setSuggestions([]);
    }
  };

  const handleSubmit = async () => {
    if (!challenge || !challengeReady || selected.length < MIN_MEMBERS || selected.length > MAX_MEMBERS) return;
    markStarted();
    setSubmitting(true);
    setError(null);
    const requestId = newRequestId();
    counters.current.requestStartedAt = performance.now();
    const partnerCount = selected.filter(entity => entity.entityType === 'Partner').length;
    const companyCount = selected.filter(entity => entity.entityType === 'Company').length;
    try {
      trackEvent({
        action: 'create_submit',
        category: 'event_group',
        value: selected.length,
        custom_parameters: { request_id: requestId, action_location: 'create_form' },
      });
    } catch {
      // Analytics is best-effort and must not affect creation.
    }

    try {
      const result = await EventFinderAPI.createEventGroup({
        requestId,
        challenge: challenge.challenge,
        entityIds: selected.map(entity => entity.entityId),
        website,
        privacyNoticeVersion: PRIVACY_NOTICE_VERSION,
      });
      try {
        trackEvent({
          action: result.outcome === 'created' ? 'create_success' : 'create_reused',
          category: 'event_group',
          value: selected.length,
          custom_parameters: { request_id: requestId, action_location: 'create_form', outcome: result.outcome },
        });
        trackApplicationInsightsEvent(collectEventGroupClientTelemetry(requestId, result.outcome, {
          ...counters.current,
          partnerCount,
          companyCount,
        }));
      } catch {
        // Analytics is best-effort and must not affect successful navigation.
      }
      navigate(result.group.canonicalUrl, { state: { creationOutcome: result.outcome } });
    } catch (submitError) {
      const apiError = submitError as ApiRequestError;
      const outcome = apiError.code || 'CLIENT_ERROR';
      try {
        trackEvent({
          action: 'create_failure',
          category: 'event_group',
          custom_parameters: { request_id: requestId, error_code: outcome, http_status: apiError.status, action_location: 'create_form', outcome: 'failed' },
        });
        trackApplicationInsightsEvent(collectEventGroupClientTelemetry(requestId, outcome, {
          ...counters.current,
          partnerCount,
          companyCount,
        }));
      } catch {
        // Analytics is best-effort and must not replace the actionable error.
      }
      setError(apiError.status === 429
        ? 'Too many groups have been created from this network. Try again in about an hour.'
        : apiError.message || 'The Event Group could not be created.');
      if (apiError.code === 'INVALID_CHALLENGE') {
        loadChallenge().catch(() => setChallenge(null));
      }
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <SEO
        title="Create an Event Group"
        description="Select Tech Companies and Corporations to create a shareable Event Group."
        noIndex
      />
      <Heading>Create an Event Group</Heading>
      <Intro>Select the Tech Companies and Corporations whose events should appear together.</Intro>

      <SearchArea>
        <Label htmlFor="event-group-search">Tech Companies and Corporations</Label>
        <SearchShell>
          {searching ? <LoaderCircle aria-hidden="true" size={18} /> : <Search aria-hidden="true" size={18} />}
          <input
            id="event-group-search"
            type="search"
            role="combobox"
            aria-autocomplete="list"
            aria-controls="event-group-results"
            aria-expanded={suggestions.length > 0}
            aria-activedescendant={highlightedIndex >= 0 ? `event-group-option-${highlightedIndex}` : undefined}
            value={query}
            disabled={selected.length >= MAX_MEMBERS}
            placeholder={selected.length >= MAX_MEMBERS ? '50-entity limit reached' : 'Search by entity name'}
            onChange={event => {
              markStarted();
              setQuery(event.target.value);
            }}
            onKeyDown={handleKeyDown}
          />
        </SearchShell>
        {suggestions.length > 0 && (
          <Results id="event-group-results" role="listbox">
            {suggestions.map((entity, index) => (
              <li key={entity.entityId} role="option" aria-selected={highlightedIndex === index}>
                <ResultButton
                  id={`event-group-option-${index}`}
                  type="button"
                  isHighlighted={highlightedIndex === index}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => addEntity(entity)}
                >
                  <EntityAvatar name={entity.displayName} entityType={entity.entityType} logoUrl={entity.logoUrl} size={40} />
                  <EntityText>
                    <EntityName>{entity.displayName}</EntityName>
                    <EntityMeta>{displayEntityType(entity.entityType)}</EntityMeta>
                  </EntityText>
                  <Count>{entity.upcomingEventCount ?? 0} upcoming</Count>
                </ResultButton>
              </li>
            ))}
          </Results>
        )}
      </SearchArea>

      <SelectionHeader>
        <SelectionTitle>Selected entities</SelectionTitle>
        <Count>{selected.length} of {MAX_MEMBERS}</Count>
      </SelectionHeader>
      {selected.length === 0 ? (
        <EmptySelection>No entities selected.</EmptySelection>
      ) : (
        <SelectionList>
          {selected.map(entity => (
            <SelectionRow key={entity.entityId}>
              <EntityAvatar name={entity.displayName} entityType={entity.entityType} logoUrl={entity.logoUrl} size={40} />
              <EntityText>
                <EntityName>{entity.displayName}</EntityName>
                <EntityMeta>{displayEntityType(entity.entityType)}</EntityMeta>
              </EntityText>
              <IconButton type="button" aria-label={`Remove ${entity.displayName}`} onClick={() => removeEntity(entity.entityId)}>
                <X aria-hidden="true" size={18} />
              </IconButton>
            </SelectionRow>
          ))}
        </SelectionList>
      )}

      <Submission>
        <Honeypot aria-hidden="true">
          Website
          <input tabIndex={-1} autoComplete="off" value={website} onChange={event => setWebsite(event.target.value)} />
        </Honeypot>
        <Notice>
          Creating a group records anonymous device diagnostics, an IP-derived approximate location, and a rotating IP hash for security. See the <Link to="/privacy">Privacy Policy</Link> and <Link to="/cookies">Cookie Policy</Link>.
        </Notice>
        {error && (
          <ErrorNotice role="alert">
            <AlertCircle aria-hidden="true" size={18} />
            <span>{error}</span>
          </ErrorNotice>
        )}
        <CreateButton
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !challengeReady || selected.length < MIN_MEMBERS || selected.length > MAX_MEMBERS}
        >
          {submitting ? <LoaderCircle className="spin" aria-hidden="true" size={18} /> : selected.length >= MIN_MEMBERS ? <Check aria-hidden="true" size={18} /> : <Plus aria-hidden="true" size={18} />}
          {submitting ? 'Creating group' : 'Create Event Group'}
        </CreateButton>
      </Submission>
    </Page>
  );
};

export default CreateEventGroupPage;